import { useState, useMemo } from "react";
import {
	SELECTED_FIELD,
	setTableConfig,
} from "../../../../components/custom-table/table-helpers";
import { useCentralStore } from "../../../../store/Central";
import { jobItemsTableConfigDefault } from "../../../../pages/job-page/job-document/job-document-components/job-items-table/job-items-table-config-default";
import { Switch } from "../../../../components/ui/switch";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Eye, EyeOff, Search, Columns3 } from "lucide-react";

export const ManageTablesTab: React.FC<{
	onClose: () => void;
}> = ({ onClose }) => {
	const { gridConfig, updateGridConfig } = useCentralStore((state) => ({
		gridConfig: state.gridConfig,
		updateGridConfig: state.updateGridConfig,
	}));

	const [searchTerm, setSearchTerm] = useState("");

	const tableConfig = useMemo(
		() => setTableConfig(jobItemsTableConfigDefault, gridConfig),
		[gridConfig]
	);

	const visibleColumns = useMemo(
		() =>
			tableConfig.columns.filter(
				(column) =>
					column.field !== SELECTED_FIELD && (column as any).show
			),
		[tableConfig.columns]
	);

	const filteredColumns = useMemo(() => {
		return tableConfig.columns
			.filter((column) => column.field !== SELECTED_FIELD)
			.filter((column) => {
				if (!searchTerm) return true;
				const title = column.title || "";
				return title.toLowerCase().includes(searchTerm.toLowerCase());
			});
	}, [tableConfig.columns, searchTerm]);

	const handleToggle = (field: string, checked: boolean) => {
		const newColumns = tableConfig.columns.map((column) => {
			if (column.field === field) {
				return { ...column, show: checked };
			}
			return column;
		});
		updateGridConfig({ ...tableConfig, columns: newColumns });
	};

	const handleToggleAll = (show: boolean) => {
		const newColumns = tableConfig.columns.map((column) => {
			if (column.field === SELECTED_FIELD) {
				return column;
			}
			return { ...column, show };
		});
		updateGridConfig({ ...tableConfig, columns: newColumns });
	};

	return (
		<div className="flex flex-col flex-1 overflow-hidden">
			{/* Header Section */}
			<div className="flex flex-col gap-4 pb-4">
				{/* Search Bar */}
				<div className="relative">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
					<Input
						type="text"
						placeholder="Spalten suchen..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="pl-10"
					/>
				</div>

				{/* Action Bar */}
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Columns3 className="h-4 w-4 text-gray-500" />
						<span className="text-sm text-gray-600">
							{visibleColumns.length} von {filteredColumns.length}{" "}
							Spalten sichtbar
						</span>
					</div>
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => handleToggleAll(true)}
							className="flex items-center gap-1"
						>
							<Eye className="h-3 w-3" />
							Alle anzeigen
						</Button>
					</div>
				</div>
			</div>

			{/* Columns List */}
			<div className="flex-1 overflow-y-auto pr-2 -mr-2">
				<div className="grid gap-2">
					{filteredColumns.map((column) => {
						const isVisible = (column as any).show;
						return (
							<div
								key={column.field}
								className={`
									flex items-center justify-between p-3 rounded-lg border
									transition-all duration-200 hover:shadow-sm
									${
										isVisible
											? "bg-white border-gray-200 hover:border-primary/30"
											: "bg-gray-50 border-gray-100 hover:border-gray-200"
									}
								`}
							>
								<div className="flex items-center gap-3 flex-1">
									<div
										className={`
										p-1.5 rounded-md transition-colors
										${isVisible ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-400"}
									`}
									>
										{isVisible ? (
											<Eye className="h-4 w-4" />
										) : (
											<EyeOff className="h-4 w-4" />
										)}
									</div>
									<Label
										htmlFor={column.field}
										className={`
											flex-1 cursor-pointer text-sm font-medium
											${isVisible ? "text-gray-900" : "text-gray-500"}
										`}
									>
										{column.title}
									</Label>
								</div>
								<Switch
									id={column.field}
									checked={isVisible}
									onCheckedChange={(checked) =>
										handleToggle(column.field!, checked)
									}
									className="ml-3"
								/>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};
